import React from 'react';
import { Card, CardBody, Icon, Tooltip } from '@repo/ui';

export interface SummaryCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: string | React.ReactNode;
  color?: string;
  bgColor?: string;
  variant?: 'default' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  valueSize?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  cardBodyClassName?: string;
  tooltip?: string;
}

/**
 * Map color name to className strings for text and background
 */
function getColorClasses(color: string): { text: string; bg: string; shadow: string } {
  const colorMap: Record<string, { text: string; bg: string }> = {
    blue: {
      text: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
    },
    green: {
      text: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-100 dark:bg-green-900/20',
    },
    yellow: {
      text: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-100 dark:bg-yellow-900/20',
    },
    purple: {
      text: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-100 dark:bg-purple-900/20',
    },
    red: {
      text: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-100 dark:bg-red-900/20',
    },
    amber: {
      text: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-100 dark:bg-amber-900/20',
    },
  };

  // Extract color from className string (e.g., "text-blue-600" -> "blue")
  if (color.includes('text-')) {
    const colorMatch = color.match(
      /text-(blue|green|yellow|purple|red|amber|xtnd-blue|xtnd-dark)-/
    );
    if (colorMatch && colorMatch[1]) {
      const colorName = colorMatch[1];

      if (colorName === 'xtnd-blue') {
        return {
          text: color,
          bg: '',
          shadow: '',
        };
      }
      if (colorName === 'xtnd-dark') {
        return {
          text: color,
          bg: '',
          shadow: '',
        };
      }
      const mapped = colorMap[colorName];
      if (mapped) {
        return {
          ...mapped,
          shadow: '',
        };
      }
    }
  }

  // If it's a color name, map it
  const mapped = colorMap[color.toLowerCase()];
  if (mapped) {
    return {
      ...mapped,
      shadow: '',
    };
  }

  // Default fallback
  return {
    text: color || 'text-xtnd-dark dark:text-xtnd-white',
    bg: '',
    shadow: '',
  };
}

export function SummaryCard({
  title,
  value,
  subtitle,
  icon,
  color,
  bgColor,
  variant = 'default',
  padding = 'md',
  valueSize = 'lg',
  className = '',
  cardBodyClassName = '',
  tooltip,
}: SummaryCardProps) {
  // Get color classes (support both color names and className strings)
  const colorClasses = color ? getColorClasses(color) : { text: '', bg: '', shadow: '' };
  const finalTextColor = colorClasses.text || color || 'text-xtnd-dark dark:text-xtnd-white';
  const finalBgColor = bgColor || colorClasses.bg || '';

  // Render icon - support both Icon component (string) and SVG (ReactNode)
  const renderIcon = () => {
    if (typeof icon === 'string') {
      // Icon component with string name - use type assertion for flexibility
      return <Icon name={icon as any} className={finalTextColor} />;
    }
    // SVG element
    return icon;
  };

  // Get padding class
  const paddingClass =
    padding === 'none' ? '' : padding === 'sm' ? 'p-2' : padding === 'md' ? 'p-4' : 'p-6';

  // Get value size class
  const valueSizeClass =
    valueSize === 'sm'
      ? 'text-lg'
      : valueSize === 'md'
        ? 'text-xl'
        : valueSize === 'lg'
          ? 'text-2xl'
          : 'text-3xl';

  // Get icon size class based on value size
  const iconSizeClass =
    valueSize === 'sm'
      ? 'h-4 w-4'
      : valueSize === 'md'
        ? 'h-5 w-5'
        : valueSize === 'lg'
          ? 'h-6 w-6'
          : 'h-6 w-6';

  return (
    <Card
      variant={variant === 'outlined' ? 'outlined' : undefined}
      className={`${className} border-transparent dark:border dark:border-gray-700 transition-all duration-200`}
    >
      <CardBody
        className={padding === 'none' ? cardBodyClassName : `${paddingClass} ${cardBodyClassName}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-1">{title}</p>
            <p className={`${valueSizeClass} font-bold ${finalTextColor} mb-1`}>{value}</p>
            <p className="text-xs text-xtnd-dark dark:text-xtnd-light">{subtitle}</p>
          </div>
          <Tooltip content={tooltip || ''} position="auto" disabled={!tooltip} maxWidth="max-w-2xl">
            <div className={`flex-shrink-0 p-2 rounded-lg ${finalBgColor} ${finalTextColor}`}>
              <div className={iconSizeClass}>{renderIcon()}</div>
            </div>
          </Tooltip>
        </div>
      </CardBody>
    </Card>
  );
}
